import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, FileCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface MediaRequirement {
  id: number;
  name: string;
  type: string;
  quantity: number;
  expected: number;
  delivered: number;
  status: string;
}

interface VisitData {
  taskId: number;
  visitId?: number;
  storeId: number;
  operatorId: number;
  campaignId?: number;
  storeName: string;
  operatorName: string;
  taskName: string;
  currentStatus: string;
  installerNote?: string;
  mediaRequirements: MediaRequirement[];
}

interface ApproveOperatorSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitData: VisitData | null;
  onSuccess: () => void;
}

export function ApproveOperatorSubmissionModal({
  isOpen,
  onClose,
  visitData,
  onSuccess
}: ApproveOperatorSubmissionModalProps) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const { toast } = useToast();

  const handleApprove = async () => {
    if (!visitData) return;
    
    setLoading(true);
    try {
      const response = await apiRequest('/api/admin-signoff/manual-signoff', 'POST', {
        taskId: visitData.taskId,
        visitId: visitData.visitId || 0,
        storeId: visitData.storeId,
        operatorId: visitData.operatorId,
        campaignId: visitData.campaignId,
        notes: notes,
        discrepancyReason: '', // No discrepancy for approvals
        completionStatus: 'admin_complete' // Always approve to admin_complete
      });

      toast({
        title: "Submission Approved",
        description: "Operator submission has been approved by admin",
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Approval error:', error);
      toast({
        title: "Approval Failed",
        description: error instanceof Error ? error.message : "Failed to approve submission",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Approve Operator Submission
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Task Info */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              Submitted Task Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p><strong>Store:</strong> {visitData?.storeName || 'Unknown'}</p>
                <p><strong>Operator:</strong> {visitData?.operatorName || 'Unknown'}</p>
              </div>
              <div>
                <p><strong>Task:</strong> {visitData?.taskName || 'Task'}</p>
                <p><strong>Status:</strong> <span className="text-orange-600 font-medium">Awaiting Admin Approval</span></p>
              </div>
            </div>
          </div>

          {visitData?.installerNote && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">Installer Note</Label>
              <div className="rounded-lg border bg-amber-50 px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap">
                {visitData.installerNote}
              </div>
            </div>
          )}

          {/* Submitted Media Formats */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Submitted Media Formats</Label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Format Name</th>
                    <th className="px-4 py-2 text-center">Expected</th>
                    <th className="px-4 py-2 text-center">Submitted</th>
                    <th className="px-4 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visitData?.mediaRequirements?.map((req, index) => (
                    <tr key={req.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 font-medium">{req.name}</td>
                      <td className="px-4 py-2 text-center">{req.expected}</td>
                      <td className="px-4 py-2 text-center font-semibold text-green-600">{req.delivered}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          req.status === 'COMPLETE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Optional Notes */}
          <div className="space-y-2">
            <Label htmlFor="approval-notes">Admin Notes (Optional)</Label>
            <Textarea
              id="approval-notes"
              placeholder="Add any notes about the approval..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={loading} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              {loading ? 'Approving...' : 'Approve Submission'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
